import { formatDateParts, t, type Messages } from '@amumin/i18n';

// Datum-/tijdhulpjes voor de ouderapp. De webapp rendert altijd in huishoudtijd (F1:
// Europe/Amsterdam, ONTWERP.md §11) en toont "Tijd in Amsterdam" als de browserzone afwijkt.
// Alleen Intl (moderne browsers); de kiosk gebruikt dit niet.

export const HOUSEHOLD_TZ = 'Europe/Amsterdam';

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  weekday: number; // ISO 1 = maandag … 7 = zondag
}

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Wandkloktijd in `tz` op instant `at`. */
export function partsIn(tz: string, at: Date = new Date()): ZonedParts {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(at)) map[part.type] = part.value;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour) % 24,
    minute: Number(map.minute),
    weekday: WEEKDAY_INDEX[map.weekday ?? ''] ?? 1,
  };
}

export function toDateKey(p: Pick<ZonedParts, 'year' | 'month' | 'day'>): string {
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** Kalenderdatum van vandaag in `tz`. */
export function todayKey(tz: string = HOUSEHOLD_TZ, at: Date = new Date()): string {
  return toDateKey(partsIn(tz, at));
}

export function isValidDateKey(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1) return false;
  const utc = new Date(Date.UTC(y, m - 1, d));
  return utc.getUTCMonth() === m - 1 && utc.getUTCDate() === d;
}

export function isValidHhmm(s: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
}

/** Kalenderrekenwerk op datums (zonder zone): `addDays('2026-12-31', 1)` → `2027-01-01`. */
export function addDays(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const utc = new Date(Date.UTC(y, m - 1, d + days));
  return toDateKey({
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  });
}

/** `b − a` in hele dagen (kalenderdatums). */
export function daysBetween(a: string, b: string): number {
  const ms = (key: string) => {
    const [y, m, d] = key.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((ms(b) - ms(a)) / 86_400_000);
}

/** ISO-weekdag (1-7) van een kalenderdatum. */
export function weekdayOf(key: string): number {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const js = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return js === 0 ? 7 : js;
}

/**
 * Begin van een nieuwe afspraak zonder gekozen tijd ("+ Afspraak", DESIGN.md §8.W3b): het volgende
 * hele uur van vandaag, zolang er daarna nog een heel uur op de dag past. Vanaf 22:00 past dat
 * niet meer (23:00 zou 23:00–23:59 worden, review c-reg r3): dan morgen 09:00, het rustige
 * standaarduur van elke andere dag. Een ouder die 's avonds plant, plant bijna altijd vooruit.
 */
export function quickStart(
  tz: string = HOUSEHOLD_TZ,
  at: Date = new Date(),
): { date: string; hhmm: string } {
  const p = partsIn(tz, at);
  const hour = p.hour + 1;
  if (hour >= 23) return { date: addDays(toDateKey(p), 1), hhmm: '09:00' };
  return { date: toDateKey(p), hhmm: `${pad2(hour)}:00` };
}

/** `HH:MM` + minuten, begrensd op 23:59 van dezelfde dag (eind van een afspraak). */
export function addMinutesToHhmm(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const total = Math.min(23 * 60 + 59, h * 60 + m + minutes);
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
}

/** UTC-offset in minuten van `tz` op instant `at` (positief oostelijk van Greenwich). */
export function offsetMinutesIn(tz: string, at: Date = new Date()): number {
  const p = partsIn(tz, at);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const truncated = Math.floor(at.getTime() / 60000) * 60000;
  return Math.round((asUtc - truncated) / 60000);
}

/** Browserzone volgens Intl; null als de browser dat niet prijsgeeft. */
export function browserZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

/**
 * Wijkt de browser af van de huishoudzone? Vergelijkt de actuele UTC-offset (een browser in
 * Brussel krijgt geen label; een browser in Londen of Curaçao wel).
 */
export function zoneDiffers(
  householdTz: string = HOUSEHOLD_TZ,
  at: Date = new Date(),
  browserOffsetMinutes: number = -at.getTimezoneOffset(),
): boolean {
  return offsetMinutesIn(householdTz, at) !== browserOffsetMinutes;
}

/** "woensdag 23 september" voor een kalenderdatum. */
export function formatDateKeyLong(key: string, m: Messages): string {
  const [y, mo, d] = key.split('-').map(Number) as [number, number, number];
  // Golf 3: andere talen via Intl in hun eigen volgorde; cijfers volgens de keuze van het gezin.
  return formatDateParts(m, { year: y, month: mo, day: d }, 'long', y !== new Date().getFullYear());
}

/**
 * Fijnslijpen (7 okt 2026): dezelfde lange datum met een hoofdletter, voor een kop of het begin van
 * een regel ("Woensdag 7 oktober"); midden in een zin blijft hij klein.
 */
export function formatDateKeyTitle(key: string, m: Messages): string {
  const text = formatDateKeyLong(key, m);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "wo 23 sep" voor een kalenderdatum (lijsten, knoppen). */
export function formatDateKeyShort(key: string, m: Messages): string {
  const [y, mo, d] = key.split('-').map(Number) as [number, number, number];
  return formatDateParts(m, { year: y, month: mo, day: d }, 'short');
}

/** "23 september" (zonder weekdag), met jaartal als dat afwijkt van dit jaar. */
export function formatDateKeyDay(key: string, m: Messages): string {
  const [y, mo, d] = key.split('-').map(Number) as [number, number, number];
  return formatDateParts(m, { year: y, month: mo, day: d }, 'day', y !== new Date().getFullYear());
}

/** Minuten sinds middernacht in `tz` (nu-lijn, "straks"). */
export function minutesNowIn(tz: string = HOUSEHOLD_TZ, at: Date = new Date()): number {
  const p = partsIn(tz, at);
  return p.hour * 60 + p.minute;
}

/** "14:05" van een instant in huishoudtijd. */
export function hhmmIn(iso: string | null | undefined, tz: string = HOUSEHOLD_TZ): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const p = partsIn(tz, at);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** Kalenderdatum van een instant in huishoudtijd. */
export function dateKeyIn(iso: string, tz: string = HOUSEHOLD_TZ): string {
  return toDateKey(partsIn(tz, new Date(iso)));
}

/** ISO-weeknummer van een kalenderdatum. */
export function isoWeekOf(key: string): number {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  const wd = date.getUTCDay() === 0 ? 7 : date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + 4 - wd);
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - yearStart) / 86400000 + 1) / 7);
}

/** Instant als "23-09-2026 14:05" in huishoudtijd. */
export function formatInstant(iso: string | null | undefined, tz: string = HOUSEHOLD_TZ): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  const p = partsIn(tz, at);
  return `${pad2(p.day)}-${pad2(p.month)}-${p.year} ${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** Instant als "23 oktober" (bv. "vertrouwd tot …"). */
export function formatInstantDate(iso: string, m: Messages, tz: string = HOUSEHOLD_TZ): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  const p = partsIn(tz, at);
  return `${p.day} ${m.months[p.month - 1] ?? ''}`;
}

/** "vandaag 15:10", "gisteren 09:05", anders "24 oktober 15:10" (huishoudtijd, DESIGN §2.9). */
export function formatInstantFriendly(
  iso: string | null | undefined,
  m: Messages,
  tz: string = HOUSEHOLD_TZ,
  now: Date = new Date(),
): string {
  if (!iso) return m.web.never;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  const p = partsIn(tz, at);
  const time = `${pad2(p.hour)}:${pad2(p.minute)}`;
  const key = toDateKey(p);
  const today = todayKey(tz, now);
  if (key === today) return m.web.todayAt.replace('{time}', time);
  if (key === addDays(today, -1)) return m.web.yesterdayAt.replace('{time}', time);
  return `${formatInstantDate(iso, m, tz)} ${time}`;
}

/** "zojuist" / "5 minuten geleden" / "2 uur geleden" / "3 dagen geleden". */
export function relativeAgo(iso: string | null, m: Messages, now: Date = new Date()): string {
  if (!iso) return m.web.never;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return m.web.never;
  const seconds = Math.max(0, Math.round((now.getTime() - at) / 1000));
  if (seconds < 60) return m.web.justNow;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t(m.web.minutesAgo, { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t(m.web.hoursAgo, { count: hours });
  return t(m.web.daysAgo, { count: Math.floor(hours / 24) });
}

/** "5 min geleden" voor het koppelscherm (started_ago_s). */
export function formatAgoSeconds(seconds: number, m: Messages): string {
  if (seconds < 60) return m.web.justNow;
  return t(m.web.minutesAgo, { count: Math.floor(seconds / 60) });
}

/**
 * 0031: periode van een vakantie of pauze, kort: "12 jul – 24 aug" (één dag: "12 jul"). Met
 * jaartal als begin en einde in verschillende jaren vallen of niet in dit jaar.
 */
export function formatRange(from: string, to: string, m: Messages, thisYear?: number): string {
  const [y1, m1, d1] = from.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = to.split('-').map(Number) as [number, number, number];
  // Zelfde regel als de server (formatRangeNl): dit jaar = geen jaartal; ander jaar = één keer aan
  // het eind ("12 jun – 29 aug 2027"); over de jaargrens = aan beide kanten.
  const one = (y: number, mo: number, d: number, year: boolean) =>
    `${d} ${m.monthsShort[mo - 1] ?? ''}${year ? ` ${y}` : ''}`;
  if (y1 !== y2) return `${one(y1, m1, d1, true)} – ${one(y2, m2, d2, true)}`;
  const otherYear = y1 !== (thisYear ?? new Date().getFullYear());
  if (from === to) return one(y1, m1, d1, otherYear);
  return `${one(y1, m1, d1, false)} – ${one(y2, m2, d2, otherYear)}`;
}

/**
 * Standaardperiode van een pauze-voorbeeld (golf 2, ronde 1): per seizoen, niet "de aangetikte dag
 * plus twee weken". Winterstop: de eerstvolgende 19 december t/m 10 januari. Zomerstop en
 * zomervakantie: 15 juni t/m 31 augustus van de eerstvolgende zomer (de zomervakantie van de regio
 * vult de editor daarna in als die bekend is). `date` is de dag van de afspraak.
 */
export function pausePresetRange(
  preset: 'zomerstop' | 'winterstop' | 'zomervakantie',
  date: string,
): { from: string; to: string } {
  const y = Number(date.slice(0, 4));
  if (preset === 'winterstop') {
    // Tot en met 10 januari hoort de dag nog bij de lopende winterstop.
    const start = date <= `${y}-01-10` ? y - 1 : y;
    return { from: `${start}-12-19`, to: `${start + 1}-01-10` };
  }
  const summer = date <= `${y}-08-31` ? y : y + 1;
  return { from: `${summer}-06-15`, to: `${summer}-08-31` };
}
